"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Check, Sparkles, Grid3X3, X, Lock, Trash2 } from "lucide-react";
import { useRims } from "@/hooks/use-rims";
import { useUserRims, useCreateUserRim, useDeleteUserRim } from "@/hooks/use-user-rims";
import { useUpload } from "@/hooks/use-upload";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
// import { getOptimizedImageUrl } from "@/lib/storage/buckets";
import type { Rim, UserRim } from "@/lib/db/schema";


interface RimSelectorProps {
  selectedRimId: string | null;
  selectedUserRimId: string | null;
  onSelectRim: (rimId: string | null, userRimId: string | null) => void;
  disabled?: boolean;
  hasPurchasedAnyPack?: boolean;
}

export function RimSelector({
  selectedRimId,
  selectedUserRimId,
  onSelectRim,
  disabled = false,
  hasPurchasedAnyPack = false,
}: RimSelectorProps) {
  const [activeTab, setActiveTab] = useState<"catalog" | "custom">("catalog");
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [newRimName, setNewRimName] = useState("");
  const [newRimSku, setNewRimSku] = useState("");
  const [newRimBrand, setNewRimBrand] = useState("");
  const [newRimStyle, setNewRimStyle] = useState("");
  const [uploadForCompany, setUploadForCompany] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [deleteDialogRim, setDeleteDialogRim] = useState<(UserRim & { generationsCount?: number }) | null>(null);

  // Fetch all rims
  const { data: allRims, isLoading: rimsLoading } = useRims();
  const { data: userRimsData, isLoading: userRimsLoading } = useUserRims();
  const createUserRim = useCreateUserRim();
  const deleteUserRim = useDeleteUserRim();
  const { upload, isUploading } = useUpload();

  // Sort rims: freepreview rims first, then others
  const rims = allRims ? [...allRims].sort((a, b) => {
    // Freepreview rims come first
    if (a.freepreview && !b.freepreview) return -1;
    if (!a.freepreview && b.freepreview) return 1;
    // Within each group, maintain original order
    return 0;
  }) : undefined;

  // Get primary company for company wheels
  const [primaryCompany, setPrimaryCompany] = useState<{ id: string; name: string } | null>(null);
  
  // Fetch user's companies on mount
  useEffect(() => {
    fetch("/api/companies")
      .then((res) => res.json())
      .then((companies) => {
        if (companies && companies.length > 0) {
          setPrimaryCompany({ id: companies[0].id, name: companies[0].name });
        }
      })
      .catch(() => {});
  }, []);

  const userRims = userRimsData?.personal || [];
  const companyRims = userRimsData?.company || [];
  // Combine personal and company rims for the custom tab
  const allCustomRims = [...userRims, ...companyRims];

  const handleRimClick = (rim: Rim) => {
    if (disabled) return;
    // If user hasn't purchased any pack, only allow freepreview rims
    if (!hasPurchasedAnyPack && !rim.freepreview) return;
    onSelectRim(rim.id, null);
  };

  const handleUserRimClick = (userRim: UserRim) => {
    if (disabled) return;
    // Only allow custom wheel selection if user has purchased a pack
    if (!hasPurchasedAnyPack) return;
    onSelectRim(null, userRim.id);
  };

  const handleUploadRim = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || !hasPurchasedAnyPack) return;
    const file = e.target.files?.[0];
    if (!file) return;

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setShowUploadForm(true);

    const result = await upload(file, "user-rims");
    if (result?.publicUrl) {
      setUploadedUrl(result.publicUrl);
    }
  };

  const handleSaveRim = async () => {
    if (!uploadedUrl || !newRimName.trim() || disabled) return;
    
    await createUserRim.mutateAsync({
      name: newRimName.trim(),
      imageUrl: uploadedUrl,
      // Explicitly set companyId: string if uploading for company, null if personal, undefined to auto-detect
      companyId: uploadForCompany && primaryCompany 
        ? primaryCompany.id 
        : primaryCompany 
          ? null // Explicitly personal if user is a company member but unchecked the box
          : undefined, // Auto-detect if not a company member
      sku: newRimSku.trim() || undefined,
      brand: newRimBrand.trim() || undefined,
      style: newRimStyle.trim() || undefined,
    });
    
    setNewRimName("");
    setNewRimSku("");
    setNewRimBrand("");
    setNewRimStyle("");
    setUploadForCompany(false);
    setPreviewUrl(null);
    setUploadedUrl(null);
    setShowUploadForm(false);
  };

  const handleCancelUpload = () => {
    setNewRimName("");
    setNewRimSku("");
    setNewRimBrand("");
    setNewRimStyle("");
    setUploadForCompany(false);
    setPreviewUrl(null);
    setUploadedUrl(null);
    setShowUploadForm(false);
  };


  return (
    <div className={cn("h-full flex flex-col pt-4 overflow-hidden", disabled && "opacity-80")}>
      {/* Tab Navigation - Simple underline style */}
      <div className="flex items-center border-b border-white/10 shrink-0">
        <button
          onClick={() => setActiveTab("catalog")}
          disabled={disabled}
          className={cn(
            "flex-1 py-2.5 text-sm font-medium transition-all duration-200 relative",
            activeTab === "catalog"
              ? "text-white"
              : "text-zinc-500 hover:text-zinc-300",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          Catalog
          {activeTab === "catalog" && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("custom")}
          disabled={disabled || !hasPurchasedAnyPack}
          className={cn(
            "flex-1 py-2.5 text-sm font-medium transition-all duration-200 relative flex items-center justify-center gap-1.5",
            activeTab === "custom"
              ? "text-white"
              : "text-zinc-500 hover:text-zinc-300",
            (disabled || !hasPurchasedAnyPack) && "cursor-not-allowed opacity-50"
          )}
          title={!hasPurchasedAnyPack ? "Get a pack to access custom wheels" : undefined}
        >
          Custom Wheels
          {!hasPurchasedAnyPack && (
            <Lock className="w-3.5 h-3.5 shrink-0" />
          )}
          {activeTab === "custom" && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
          )}
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col min-h-0 mt-2 overflow-hidden">
        {activeTab === "catalog" ? (
          <>
            {/* Rim Grid - Optimized for maximum rows */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pl-2 pr-1 -mr-1 rim-scroll">
              {rimsLoading ? (
                <div className="grid grid-cols-3 gap-2 px-2 pb-1 pt-1">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-white/5 animate-pulse rounded-xl"
                      style={{ animationDelay: `${i * 50}ms` }}
                    />
                  ))}
                </div>
              ) : rims?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <Grid3X3 className="w-8 h-8 text-zinc-600" />
                  </div>
                  <p className="text-sm text-zinc-400">No wheels in the catalog yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 px-2 pb-1 pt-1">
                  {rims?.map((rim, index) => {
                    const isLocked = !hasPurchasedAnyPack && !rim.freepreview;
                    const isDisabled = disabled || isLocked;
                    return (
                      <button
                        key={rim.id}
                        onClick={() => handleRimClick(rim)}
                        disabled={isDisabled}
                        className={cn(
                          "group relative aspect-square rounded-xl overflow-hidden transition-all duration-300",
                          "bg-linear-to-br from-zinc-800/50 to-zinc-900/50",
                          !isDisabled && "hover:scale-[1.02] hover:shadow-xl hover:shadow-black/30",
                          isDisabled && "cursor-not-allowed",
                          isLocked && "opacity-30",
                          selectedRimId === rim.id
                            ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-zinc-950 shadow-lg shadow-blue-500/20"
                            : "ring-1 ring-white/10 hover:ring-white/20"
                        )}
                        style={{ animationDelay: `${index * 30}ms` }}
                        title={isLocked ? "Get a pack to unlock more wheels" : undefined}
                      >
                        {/* Shine effect on hover */}
                        {!isDisabled && (
                          <div className="absolute inset-0 bg-linear-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        )}
                        
                        <Image
                          src={rim.imageUrl}
                          alt={rim.name}
                          fill
                          sizes="(max-width: 1024px) 33vw, 200px"
                          className={cn(
                            "object-cover transition-transform duration-500",
                            !isDisabled && "group-hover:scale-110",
                            isLocked && "grayscale-[0.3]"
                          )}
                          loading={index < 9 ? "eager" : "lazy"}
                          priority={index < 6}
                        />

                        {/* Lock overlay for disabled rims */}
                        {isLocked && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px]">
                            <div className="bg-black/60 rounded-full p-2">
                              <Lock className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        )}

                        {/* Selection overlay - subtle blue tint when wheel is selected */}
                        {selectedRimId === rim.id && !isLocked && (
                          <div className="absolute inset-0 bg-blue-500/20 backdrop-blur-[0.5px] pointer-events-none z-5" />
                        )}

                        {/* Selection indicator */}
                        {selectedRimId === rim.id && (
                          <div className="absolute top-1.5 right-1.5 bg-blue-500 text-white rounded-full p-1 shadow-lg animate-in zoom-in duration-200 z-10">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </div>
                        )}
                        
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Upload Button/Form */}
            {showUploadForm ? (
              <div className="mb-3 p-4 rounded-xl bg-linear-to-br from-zinc-800/50 to-zinc-900/50 border border-white/10 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  {previewUrl && (
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-white/5 shrink-0 ring-1 ring-white/10">
                      <Image
                        src={previewUrl}
                        alt="Preview"
                        fill
                        sizes="80px"
                        className="object-cover"
                      />
                      {(isUploading || createUserRim.isPending) && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm">
                          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex-1 space-y-3">
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="rim-name" className="text-xs text-zinc-400 mb-1.5 block">
                          Name your wheel
                        </Label>
                        <Input
                          id="rim-name"
                          value={newRimName}
                          onChange={(e) => setNewRimName(e.target.value)}
                          placeholder="e.g., Vossen CVT"
                          className="h-9 bg-white/5 border-white/10 text-sm placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                          disabled={disabled}
                        />
                      </div>
                      {primaryCompany && (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="upload-for-company"
                            checked={uploadForCompany}
                            onChange={(e) => setUploadForCompany(e.target.checked)}
                            className="w-4 h-4 rounded border-white/10 bg-white/5 text-blue-500 focus:ring-blue-500/20"
                            disabled={disabled}
                          />
                          <Label htmlFor="upload-for-company" className="text-xs text-zinc-400 cursor-pointer">
                            Upload to {primaryCompany.name} library
                          </Label>
                        </div>
                      )}
                      {uploadForCompany && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label htmlFor="rim-brand" className="text-xs text-zinc-400 mb-1.5 block">
                              Brand (optional)
                            </Label>
                            <Input
                              id="rim-brand"
                              value={newRimBrand}
                              onChange={(e) => setNewRimBrand(e.target.value)}
                              placeholder="e.g., Vossen"
                              className="h-9 bg-white/5 border-white/10 text-sm placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                              disabled={disabled}
                            />
                          </div>
                          <div>
                            <Label htmlFor="rim-sku" className="text-xs text-zinc-400 mb-1.5 block">
                              SKU (optional)
                            </Label>
                            <Input
                              id="rim-sku"
                              value={newRimSku}
                              onChange={(e) => setNewRimSku(e.target.value)}
                              placeholder="e.g., CVT-20"
                              className="h-9 bg-white/5 border-white/10 text-sm placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                              disabled={disabled}
                            />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="rim-style" className="text-xs text-zinc-400 mb-1.5 block">
                              Style (optional)
                            </Label>
                            <Input
                              id="rim-style"
                              value={newRimStyle}
                              onChange={(e) => setNewRimStyle(e.target.value)}
                              placeholder="e.g., Concave"
                              className="h-9 bg-white/5 border-white/10 text-sm placeholder:text-zinc-600 focus:border-blue-500/50 focus:ring-blue-500/20"
                              disabled={disabled}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8 bg-transparent border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white"
                        onClick={handleCancelUpload}
                        disabled={disabled || createUserRim.isPending}
                      >
                        <X className="w-3 h-3 mr-1.5" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 h-8 bg-blue-600 hover:bg-blue-500 text-white"
                        disabled={disabled || !uploadedUrl || !newRimName.trim() || createUserRim.isPending || isUploading}
                        onClick={handleSaveRim}
                      >
                        {createUserRim.isPending ? (
                          <>
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5" />
                            Saving
                          </>
                        ) : isUploading ? (
                          "Uploading..."
                        ) : (
                          <>
                            <Check className="w-3 h-3 mr-1.5" />
                            Save
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : !hasPurchasedAnyPack ? (
              <div className="mb-3 p-4 rounded-xl bg-linear-to-br from-zinc-800/50 to-zinc-900/50 border border-white/10 backdrop-blur-sm">
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3">
                    <Lock className="w-6 h-6 text-zinc-500" />
                  </div>
                  <p className="text-sm font-medium text-zinc-300 mb-1">Get a pack to upload custom wheels</p>
                  <p className="text-xs text-zinc-500">Upgrade to access custom wheels</p>
                </div>
              </div>
            ) : (
              <label className={cn("group cursor-pointer mb-3", disabled && "cursor-not-allowed pointer-events-none")}>
                <div className={cn(
                  "flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-dashed border-white/10 bg-white/2 transition-all duration-300",
                  !disabled && "hover:border-blue-500/50 hover:bg-blue-500/5",
                  disabled && "opacity-50"
                )}>
                  <div className={cn(
                    "w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center transition-colors",
                    !disabled && "group-hover:bg-blue-500/20"
                  )}>
                    <Plus className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className={cn(
                    "text-sm font-medium transition-colors",
                    !disabled ? "text-zinc-400 group-hover:text-white" : "text-zinc-600"
                  )}>
                    {disabled ? "Generation in Progress" : "Upload Custom Wheel"}
                  </span>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleUploadRim}
                  disabled={disabled}
                />
              </label>
            )}


            {/* Custom Rim Grid (Personal + Company) */}
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pl-2 pr-1 -mr-1 rim-scroll">
              {userRimsLoading ? (
                <div className="grid grid-cols-3 gap-2 px-2 pb-1">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-white/5 animate-pulse rounded-xl"
                      style={{ animationDelay: `${i * 50}ms` }}
                    />
                  ))}
                </div>
              ) : allCustomRims.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-zinc-600" />
                  </div>
                  <p className="text-sm text-zinc-500">No custom wheels yet</p>
                  <p className="text-xs text-zinc-600 mt-1">Upload your own wheels or use company wheels</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 px-2 pb-1">
                  {allCustomRims.map((rim, index) => {
                    const isCompanyRim = rim.companyId !== null;
                    const isDisabled = disabled || !hasPurchasedAnyPack;
                    return (
                      <button
                        key={rim.id}
                        onClick={() => handleUserRimClick(rim)}
                        disabled={isDisabled}
                        className={cn(
                          "group relative aspect-square rounded-xl overflow-hidden transition-all duration-300",
                          "bg-linear-to-br from-zinc-800/50 to-zinc-900/50",
                          !isDisabled && "hover:scale-[1.02] hover:shadow-xl hover:shadow-black/30",
                          isDisabled && "cursor-not-allowed",
                          !hasPurchasedAnyPack && "opacity-60",
                          selectedUserRimId === rim.id
                            ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-zinc-950 shadow-lg shadow-blue-500/20"
                            : "ring-1 ring-white/10 hover:ring-white/20"
                        )}
                        style={{ animationDelay: `${index * 30}ms` }}
                        title={!hasPurchasedAnyPack ? "Get a pack to access custom wheels" : undefined}
                      >
                        {!isDisabled && (
                          <div className="absolute inset-0 bg-linear-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        )}
                        
                        <Image
                          src={rim.imageUrl}
                          alt={rim.name}
                          fill
                          sizes="(max-width: 1024px) 33vw, 200px"
                          className={cn(
                            "object-cover transition-transform duration-500",
                            !isDisabled && "group-hover:scale-110",
                            !hasPurchasedAnyPack && "grayscale-[0.3]"
                          )}
                          loading="lazy"
                        />
                        
                        {/* Lock overlay for disabled custom rims */}
                        {!hasPurchasedAnyPack && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px]">
                            <div className="bg-black/60 rounded-full p-2">
                              <Lock className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        )}
                        
                        {/* Generation count badge - show for all wheels with generations */}
                        {!isCompanyRim && rim.generationsCount > 0 && (
                          <div className="absolute top-1.5 left-1.5 bg-blue-500/90 text-white text-[10px] font-medium px-1.5 py-0.5 rounded shadow-lg backdrop-blur-sm z-10">
                            {rim.generationsCount}× used
                          </div>
                        )}

                        {/* Company rim indicator */}
                        {isCompanyRim && (
                          <div className="absolute top-1.5 left-1.5 bg-violet-500/90 text-white text-[10px] font-medium px-1.5 py-0.5 rounded shadow-lg backdrop-blur-sm z-10">
                            Company
                          </div>
                        )}

                        {selectedUserRimId === rim.id && (
                          <div className="absolute top-1.5 right-1.5 bg-blue-500 text-white rounded-full p-1 shadow-lg animate-in zoom-in duration-200 z-10">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </div>
                        )}

                        {/* Delete button - ONLY for personal rims (companyId is NULL) */}
                        {!isCompanyRim && hasPurchasedAnyPack && !isDisabled && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteDialogRim(rim);
                            }}
                            className="absolute bottom-1.5 right-1.5 bg-red-500/80 hover:bg-red-500 active:bg-red-600 p-1.5 rounded shadow-lg backdrop-blur-sm transition-all z-10 cursor-pointer opacity-70 md:opacity-0 md:group-hover:opacity-100"
                            title="Delete wheel"
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </div>
                        )}

                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Disclaimer - Compact at bottom - only show for catalog wheels */}
      {activeTab === "catalog" && (
        <div className="mt-2 pt-2 border-t border-white/5 shrink-0">
          <p className="text-[10px] leading-4 text-zinc-600">
            We do not claim ownership of any wheel designs. Images are provided only for visualization and are not downloadable assets.
          </p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialogRim} onOpenChange={() => setDeleteDialogRim(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Custom Wheel</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deleteDialogRim?.name}&quot;?
              {deleteDialogRim?.generationsCount && deleteDialogRim.generationsCount > 0 && (
                <span className="block mt-2 text-amber-400">
                  This wheel was used in {deleteDialogRim.generationsCount} generation(s).
                  Those generations will show &quot;Wheel (deleted)&quot; instead.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogRim(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteDialogRim) {
                  deleteUserRim.mutate(deleteDialogRim.id);
                  setDeleteDialogRim(null);
                }
              }}
              disabled={deleteUserRim.isPending}
            >
              {deleteUserRim.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
